import { ColumnDef, type ColumnFiltersState } from "@tanstack/react-table";
import { differenceInDays } from "date-fns";
import {
  ApiCircuit,
  ApiLine,
  ApiRouteLabelConfig,
  ApiRouteLabelInline,
  ApiVenueStaffInfo,
  ApiZone,
} from "griptonite-api-client";
import { useMemo } from "react";

import { useRouteLabelConfigQuery } from "@/hooks/routeLabels";

import { Route } from "../../core/types";
import { useVenueCircuitsQuery, useVenueLinesQuery, useVenueZonesQuery } from "../../hooks/routes";
import { useDeepMemo } from "../../hooks/useDeepMemo";
import { useCurrentClimbType, useCurrentVenueUuid, useVenueStaffQuery } from "../../hooks/venues";
import { convertToInternal } from "../../utils/grades";
import { getTraitChoices } from "../../utils/traits";
import { buildLabelList, splitRouteLabelConfig } from "../RouteLabels/routeLabelsConfig";

export type RangeFilter<T> = {
  min?: T;
  max?: T;
};

export type GradeFilter = RangeFilter<string> & { system: string };

export type MultipleChoiceFilter<T> = {
  matches: Set<T>;
};

export type FilterParams = {
  zone: MultipleChoiceFilter<string | null>;
  grade: GradeFilter;
  setters: MultipleChoiceFilter<string | null>;
  competitionCategories: MultipleChoiceFilter<string | null>;
  startTime: RangeFilter<string>;
  endTime: RangeFilter<string>;
  starRating: RangeFilter<number>;
  age: RangeFilter<number>;
  timeLeft: RangeFilter<number>;
  circuit: MultipleChoiceFilter<string | null>;
  startLine: MultipleChoiceFilter<string | null>;
  searchQuery: string;
  styles: MultipleChoiceFilter<string | null>;
  skills: MultipleChoiceFilter<string | null>;
  holds: MultipleChoiceFilter<string | null>;
  holdColors: MultipleChoiceFilter<string>;
  labels: MultipleChoiceFilter<string | null>;
  isExComp: boolean;
  isRecentlyRestored: boolean;
  hasComments: boolean;
  ascentStyle: MultipleChoiceFilter<"AUTOBELAY" | "LEAD_TOPROPE" | "TOPROPE" | "LEAD">;
};

// e.g. RangeFilterValueType<"startTime"> = string;
export type RangeFilterValueType<K extends keyof FilterParams> =
  FilterParams[K] extends RangeFilter<infer T> ? T : never;

export const isMultipleChoiceFilter = (filter: unknown): filter is MultipleChoiceFilter<unknown> =>
  typeof filter === "object" &&
  !!filter &&
  "matches" in filter &&
  typeof filter.matches === "object";

export const isRangeFilter = (filter: unknown): filter is RangeFilter<unknown> =>
  typeof filter === "object" && !!filter && ("min" in filter || "max" in filter);

export type FilterQuery = Partial<FilterParams>;

/**
 * A label's value in the labels filter and facets: `<group>/<label uuid>`. A
 * root label is its own group, so it uses its own uuid there. Keeping the group
 * in the value lets the filter match within and across groups without the
 * label config to hand.
 */
export const labelFilterValue = (label: Pick<ApiRouteLabelInline, "uuid" | "group">) =>
  `${label.group?.uuid ?? label.uuid}/${label.uuid}`;

const dateStringInRange = (dateString: string | null, min?: string, max?: string) => {
  if (dateString === null) {
    return false;
  }
  const date = new Date(dateString);
  if (min && date < new Date(min)) {
    return false;
  }
  if (max && date > new Date(max)) {
    return false;
  }
  return true;
};

export const isValidFilterName = (key: string): key is keyof FilterParams =>
  key in availableFilters;

export const getFilterType = (key: keyof FilterParams) => availableFilters[key].type;

export const getFilterLabel = (key: keyof FilterParams) => availableFilters[key].label;

type AvailableFilters = typeof availableFilters;
type FilterType = AvailableFilters[keyof FilterParams]["type"];

/** Names of the filters whose definition has the given type. */
export type FilterNameOfType<T extends FilterType> = {
  [K in keyof FilterParams]: AvailableFilters[K]["type"] extends T ? K : never;
}[keyof FilterParams];

export const isFilterOfType = <T extends FilterType>(
  key: keyof FilterParams,
  type: T,
): key is FilterNameOfType<T> => availableFilters[key].type === type;

export type RangeFilterName = keyof {
  [K in keyof FilterParams as FilterParams[K] extends RangeFilter<unknown> ? K : never]: true;
};

export const isRangeFilterName = (key: keyof FilterParams): key is RangeFilterName =>
  isFilterOfType(key, "range") || isFilterOfType(key, "grade") || isFilterOfType(key, "dateRange");

export const getRangeFilterChoices = (key: RangeFilterName) => {
  const filter = availableFilters[key];
  return "choices" in filter ? filter.choices : undefined;
};

const availableFilters = {
  zone: {
    func: (route, { matches }) => matches.has(route.zone && route.zone.uuid),
    type: "multipleChoice",
    label: "Zone",
  },

  grade: {
    type: "grade",
    func: (route, { min, max, system }) => {
      const minInternal = min !== undefined ? convertToInternal(min, system) : null;
      const maxInternal = max !== undefined ? convertToInternal(max, system) : null;
      if (minInternal !== null && route.gradeInternal && route.gradeInternal < minInternal) {
        return false;
      }
      if (maxInternal !== null && route.gradeInternal && route.gradeInternal > maxInternal) {
        return false;
      }
      return true;
    },
    label: "Grade",
  },

  competitionCategories: {
    type: "multipleChoice",
    func: (route, { matches }) =>
      route.competitionRouteGroups.some((group) =>
        group.categories.some((categoryUuid) =>
          matches.has(group.competition + ":" + categoryUuid),
        ),
      ),
    label: "Categories",
  },

  setters: {
    type: "multipleChoice",
    func: (route, { matches }) =>
      route.setters.length === 0
        ? matches.has(null)
        : route.setters.some((setter) => matches.has(setter.uuid)),
    label: "Setters",
  },

  holdColors: {
    type: "multipleChoice",
    func: (route, { matches }) => !!route.colors?.split(":").some((color) => matches.has(color)),
    label: "Hold Colors",
  },

  // Picking several options in one group finds routes with any of them. Picking
  // in more than one group finds routes that match in every group.
  labels: {
    type: "multipleChoice",
    func: (route, { matches }) => {
      if (route.labels.length === 0) {
        return matches.has(null);
      }
      const wantedByGroup = new Map<string, string[]>();
      for (const value of matches) {
        if (value === null) {
          continue;
        }
        const groupKey = value.split("/")[0];
        wantedByGroup.set(groupKey, [...(wantedByGroup.get(groupKey) ?? []), value]);
      }
      if (wantedByGroup.size === 0) {
        return false;
      }
      const routeValues = new Set(route.labels.map(labelFilterValue));
      return [...wantedByGroup.values()].every((values) =>
        values.some((value) => routeValues.has(value)),
      );
    },
    label: "Labels",
  },

  starRating: {
    type: "range",
    func: (route, { min, max }) => {
      const rating = route.feedback?.averageStarRating || 0;
      return rating >= (min ?? 0) && rating <= (max ?? 5);
    },
    label: "Star Rating",
    choices: [
      {
        value: 0,
        label: "Unrated",
      },
      {
        value: 1,
        label: "1⭐️",
      },
      {
        value: 1.5,
        label: "1.5⭐️",
      },
      {
        value: 2,
        label: "2⭐️",
      },
      {
        value: 2.5,
        label: "2.5⭐️",
      },
      {
        value: 3,
        label: "3⭐️",
      },
      {
        value: 3.5,
        label: "3.5⭐️",
      },
      {
        value: 4,
        label: "4⭐️",
      },
      {
        value: 4.5,
        label: "4.5⭐️",
      },
      {
        value: 5,
        label: "5⭐️",
      },
    ],
  },

  ascentStyle: {
    type: "multipleChoice",
    func: (route, { matches }) => {
      if (route.autoBelay) {
        return matches.has("AUTOBELAY");
      } else if (route.canLead) {
        return matches.has("LEAD_TOPROPE");
      } else {
        return matches.has("TOPROPE");
      }
    },
    label: "Ascent Style",
  },

  isExComp: {
    type: "boolean",
    func: (route, isExComp) =>
      (!!route.competitionRouteGroups.length && !!(route.circuit || route.startLine)) === isExComp,
    label: "Ex Comp",
  },

  age: {
    type: "range",
    func: (route, { min, max }) => {
      if (!route.startTime) {
        return false;
      }
      const age = differenceInDays(new Date(), new Date(route.startTime));
      return age >= (min ?? 0) && age <= (max ?? Infinity);
    },
    label: "Age",
    choices: [
      { value: 0, label: "New" },
      { value: 1, label: "1 day" },
      { value: 7, label: "1 week" },
      { value: 14, label: "2 weeks" },
      { value: 30, label: "30 days" },
      { value: 90, label: "90 days" },
      { value: 183, label: "6 months" },
      { value: Infinity, label: "6+ months" },
    ],
    reverseSlider: true,
  },

  timeLeft: {
    type: "range",
    func: (route, { min, max }) => {
      const timeUntilStrip = route.scheduledEndTime
        ? differenceInDays(new Date(route.scheduledEndTime), new Date())
        : Infinity;
      return timeUntilStrip >= (min ?? 0) && timeUntilStrip <= (max ?? Infinity);
    },
    label: "Time left",
    choices: [
      { value: 0, label: "Today" },
      { value: 1, label: "1 day" },
      { value: 7, label: "1 week" },
      { value: 14, label: "2 weeks" },
      { value: 30, label: "30 days" },
      { value: 90, label: "90 days" },
      { value: 183, label: "6 months" },
      { value: Infinity, label: "6+ months" },
    ],
  },

  circuit: {
    type: "multipleChoice",
    func: (route, { matches }) => {
      if (route.circuit) {
        return matches.has(route.circuit.uuid);
      } else if (route.competitionRouteGroups.length) {
        return matches.has(route.competitionRouteGroups[0].competition);
      } else {
        return matches.has(null);
      }
    },
    label: "Circuit",
  },

  startLine: {
    type: "multipleChoice",
    func: (route, { matches }) => {
      if (route.startLine) {
        return matches.has(route.startLine.uuid);
      } else if (route.competitionRouteGroups.length) {
        return matches.has(route.competitionRouteGroups[0].competition);
      } else {
        return matches.has(null);
      }
    },
    label: "Line",
  },

  styles: {
    type: "multipleChoice",
    func: (route, { matches }) =>
      route.styles.length === 0
        ? matches.has(null)
        : route.styles.some((style) => matches.has(style)),
    label: "Styles",
  },

  skills: {
    type: "multipleChoice",
    func: (route, { matches }) =>
      route.skills.length === 0
        ? matches.has(null)
        : route.skills.some((skill) => matches.has(skill)),
    label: "Skills",
  },

  holds: {
    type: "multipleChoice",
    func: (route, { matches }) =>
      route.holds.length === 0 ? matches.has(null) : route.holds.some((hold) => matches.has(hold)),
    label: "Holds",
  },

  searchQuery: {
    type: "search",
    func: (route, query) => {
      if (!query) {
        return true;
      }
      const lowerCaseQ = query.toLowerCase().trim();
      return !!(
        route.name?.toLowerCase().includes(lowerCaseQ) ||
        route.gradeValue?.toLowerCase() === lowerCaseQ ||
        route.pinGradeValue?.toLowerCase() === lowerCaseQ ||
        route.zone?.name.toLowerCase().includes(lowerCaseQ) ||
        route.circuit?.name?.toLowerCase().includes(lowerCaseQ) ||
        route.circuit?.number?.toString() === lowerCaseQ ||
        route.startLine?.name.toLowerCase().includes(lowerCaseQ) ||
        route.setters.some((setter) => setter.name.toLowerCase().includes(lowerCaseQ)) ||
        route.competitionRouteGroups.some(
          (group) =>
            group.competitionName.toLowerCase().includes(lowerCaseQ) ||
            group.leagueName?.toLowerCase().includes(lowerCaseQ),
        )
      );
    },
    label: "Search",
  },

  startTime: {
    label: "Set date",
    type: "dateRange",
    func: (route, { min, max }) => {
      if (route.startTime) {
        return dateStringInRange(route.startTime, min, max);
      }
      return false;
    },
  },

  endTime: {
    label: "Strip date",
    type: "dateRange",
    func: (route, { min, max }) => {
      if (route.scheduledEndTime) {
        return dateStringInRange(route.scheduledEndTime, min, max);
      }
      return false;
    },
  },

  isRecentlyRestored: {
    type: "boolean",
    func: (route, isRecentlyRestored) => {
      const routeIsRestoredLast7d =
        route.restoreTime !== null &&
        new Date(route.restoreTime).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000;
      return routeIsRestoredLast7d === isRecentlyRestored;
    },
    label: "Restored in last 7d",
  },

  hasComments: {
    type: "boolean",
    func: (route, hasComments) => (route.pendingCommentsCount ?? 0) > 0 === hasComments,
    label: "Has comments in inbox",
  },
} as const satisfies {
  [k in keyof FilterParams]: {
    type: "range" | "grade" | "multipleChoice" | "search" | "boolean" | "dateRange";
    func: (route: Route, filterParams: FilterParams[k]) => boolean;
    label: string;
    choices?: FilterParams[k] extends RangeFilter<unknown>
      ? { value: RangeFilterValueType<k>; label: string }[]
      : never;
    reverseSlider?: boolean;
  };
};

const routeFacetFunctions = {
  zone: (route: Route) => ({
    value: route.zone && route.zone.uuid,
    name: route.zone ? route.zone.name : route.climbType === "BOULDER" ? "No zone" : "No sector",
  }),
  setters: (route: Route) =>
    route.setters.length
      ? route.setters.map((setter) => ({
          name: setter.name,
          value: setter.uuid,
        }))
      : {
          name: "No setters",
          value: null,
        },
  competitionCategories: (route: Route) =>
    route.competitionRouteGroups.map((crg) => ({
      name: crg.name,
      // Include the competition UUID so that we can hackily filter the displayed facets
      // based on the currently selected competition
      value: crg.competition + ":" + crg.categories[0],
    })),
  // circuit: (route: ApiRouteDetail) => route.circuit && route.circuit.uuid,
  circuit: (route: Route) => ({
    value: route.circuit
      ? route.circuit.uuid
      : route.competitionRouteGroups[0]?.competition || null,
    name: route.circuit
      ? route.circuit.name
      : route.competitionRouteGroups[0]?.competitionName
        ? `Event: ${route.competitionRouteGroups[0]?.competitionName}`
        : "No circuit",
  }),
  startLine: (route: Route) => ({
    // route.startLine && route.startLine.uuid,
    // value: route.startLine && route.startLine.uuid,
    value: route.startLine
      ? route.startLine.uuid
      : route.competitionRouteGroups[0]?.competition || null,
    name: route.startLine
      ? route.startLine.name
      : route.competitionRouteGroups[0]?.competitionName
        ? `Event: ${route.competitionRouteGroups[0]?.competitionName}`
        : "No line",
  }),
  styles: (route: Route) =>
    route.styles.length
      ? route.styles.map((style) => ({ name: style, value: style }))
      : { name: "No styles", value: null },
  skills: (route: Route) =>
    route.skills.length
      ? route.skills.map((skill) => ({ name: skill, value: skill }))
      : { name: "No skills", value: null },
  holds: (route: Route) =>
    route.holds.length
      ? route.holds.map((hold) => ({ name: hold, value: hold }))
      : { name: "No holds", value: null },
  labels: (route: Route) =>
    route.labels.length
      ? route.labels.map((label) => ({
          name: label.group ? `${label.group.name}: ${label.name}` : label.name,
          value: labelFilterValue(label),
        }))
      : { name: "No labels", value: null },
  holdColors: (route: Route) =>
    (route.colors?.split(":") || ["#FFFFFF"]).map((color) => ({
      name: color,
      value: color,
    })),
  ascentStyle: (route: Route) => ({
    value: route.autoBelay ? "Auto-belay" : route.canLead ? "Lead" : "Top-rope",
    name: route.autoBelay ? "Auto-belay" : route.canLead ? "Lead" : "Top-rope",
  }),
};

const applyFilter = (routes: Route[], filter: FilterQuery) => {
  return routes.filter((route) =>
    Object.entries(filter).every(([key, value]) => {
      if (value === undefined) {
        return true;
      }
      const filterFunc = availableFilters[key as keyof FilterQuery].func as (
        route: Route,
        filterParams: FilterQuery[keyof FilterQuery],
      ) => boolean;
      return filterFunc(route, value);
    }),
  );
};

export default function useFilteredRoutes(routes: Route[], filter: FilterQuery) {
  return useDeepMemo(applyFilter, routes, filter);
}

export const filterParamsToColumnFilters = (
  filter: FilterQuery,
  // oxlint-disable-next-line typescript/no-explicit-any
  columns: ColumnDef<Route, any>[],
): ColumnFiltersState => {
  const result: ColumnFiltersState = [];
  for (const [key, value] of Object.entries(filter)) {
    const column = columns.find((col) => col.id === key);
    if (!column) {
      continue;
    }
    result.push({
      id: key,
      value,
    });
  }
  return result;
};

export type FacetMap<T> = Map<T, { value: T; name: string; count: number }>;

export type RoutesFacets = {
  circuit: FacetMap<string | null>;
  setters: FacetMap<string | null>;
  zone: FacetMap<string | null>;
  startLine: FacetMap<string | null>;
  styles: FacetMap<string | null>;
  skills: FacetMap<string | null>;
  holds: FacetMap<string | null>;
  holdColors: FacetMap<string>;
  labels: FacetMap<string | null>;
  competitionCategories: FacetMap<string>;
  gradeValue: FacetMap<string | null>;
  ascentStyle: FacetMap<"AUTOBELAY" | "LEAD_TOPROPE" | "TOPROPE" | null>;
};

const emptyArray = [] as never[];

type FacetsInitialParams = {
  circuits?: ApiCircuit[];
  lines?: ApiLine[];
  zones?: ApiZone[];
  setters?: ApiVenueStaffInfo[];
  labelConfig?: ApiRouteLabelConfig;
};

const getFacetsInitial = ({
  circuits,
  lines,
  zones,
  setters,
  labelConfig,
}: FacetsInitialParams): RoutesFacets => {
  const { activeGroups, activeRootLabels } = splitRouteLabelConfig(labelConfig);
  // In the order of the labels settings page, with unused labels at 0.
  const labelFacets = buildLabelList(activeGroups, activeRootLabels).flatMap((entry) =>
    entry.kind === "group"
      ? entry.group.labels.map((label) => ({
          name: `${entry.group.name}: ${label.name}`,
          value: labelFilterValue({ uuid: label.uuid, group: entry.group }),
        }))
      : [{ name: entry.label.name, value: labelFilterValue({ ...entry.label, group: null }) }],
  );
  return {
    circuit: new Map(
      circuits?.map((circuit) => [
        circuit.uuid,
        { value: circuit.uuid, name: circuit.name, count: 0 },
      ]) || emptyArray,
    ),
    setters: new Map(
      setters?.map((setter) => [
        setter.user.uuid,
        { value: setter.user.uuid, name: setter.user.name, count: 0 },
      ]) || emptyArray,
    ),
    zone: new Map(
      zones?.map((zone) => [zone.uuid, { value: zone.uuid, name: zone.name, count: 0 }]) ||
        emptyArray,
    ),
    startLine: new Map(
      lines?.map((line) => [line.uuid, { value: line.uuid, name: line.name, count: 0 }]) ||
        emptyArray,
    ),
    styles: new Map(
      getTraitChoices("styles").map((style) => [style, { value: style, name: style, count: 0 }]),
    ),
    skills: new Map(
      getTraitChoices("skills").map((skill) => [skill, { value: skill, name: skill, count: 0 }]),
    ),
    holds: new Map(
      getTraitChoices("holds").map((hold) => [hold, { value: hold, name: hold, count: 0 }]),
    ),
    holdColors: new Map(),
    labels: new Map(labelFacets.map((facet) => [facet.value, { ...facet, count: 0 }] as const)),
    competitionCategories: new Map(),
    // Only the archive's server-side facets include grades.
    gradeValue: new Map(),
    ascentStyle: new Map(),
  };
};

type MapKey<M extends Map<unknown, unknown>> = Parameters<M["set"]>[0];

const getFacets = (routes: Route[], otherData: FacetsInitialParams) => {
  const facets = getFacetsInitial(otherData);

  const incrementFacetCount = <T extends keyof RoutesFacets>(
    facetKey: T,
    name: string | undefined,
    value: MapKey<RoutesFacets[T]>,
  ) => {
    const facet = facets[facetKey] as FacetMap<MapKey<RoutesFacets[T]>>;
    const facetValue = facet.get(value);
    if (facetValue) {
      facetValue.count++;
    } else {
      facet.set(value, { value, name: name || "Unknown", count: 1 });
    }
  };

  routes.forEach((route) => {
    Object.entries(routeFacetFunctions).forEach(([key, fn]) => {
      const result = fn(route);
      if (Array.isArray(result)) {
        result.length
          ? result.forEach((v) => {
              incrementFacetCount(key as keyof RoutesFacets, v.name, v.value);
            })
          : incrementFacetCount(key as keyof RoutesFacets, "<no value>", null);
      } else {
        incrementFacetCount(key as keyof RoutesFacets, result.name ?? undefined, result.value);
      }
    });
  });

  return facets;
};

export const useRoutesFacets = (routes: Route[]) => {
  const climbType = useCurrentClimbType();
  const venueUuid = useCurrentVenueUuid();
  const { data: circuits } = useVenueCircuitsQuery(climbType === "BOULDER" ? venueUuid : undefined);
  const { data: lines } = useVenueLinesQuery(climbType === "SPORT" ? venueUuid : undefined);
  const { data: zones } = useVenueZonesQuery(venueUuid);
  const { data: setters } = useVenueStaffQuery(venueUuid);
  const { data: labelConfig } = useRouteLabelConfigQuery();

  const otherData = useMemo(
    () => ({ circuits, lines, zones, setters, labelConfig }),
    [circuits, lines, zones, setters, labelConfig],
  );

  return useDeepMemo(getFacets, routes, otherData);
};
